import * as Browser from '../util/browser';
import * as Util from '../util/util';
import * as DomEvent from '../util/dom-event';
import * as DomUtil from '../util/dom-util';
import { Grid, GridOptions } from './grid';
/*
 * @class TileLayer
 * @inherits GridLayer
 * @aka L.TileLayer
 * Used to load and display tile layers on the map. Note that most tile servers require attribution, which you can set under `Layer`. Extends `GridLayer`.
 *
 * @example
 *
 * ```js
 * L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png?{foo}', {foo: 'bar', attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'}).addTo(map);
 * ```
 *
 * @section URL template
 * @example
 *
 * A string of the following form:
 *
 * ```
 * 'http://{s}.somedomain.com/blabla/{z}/{x}/{y}{r}.png'
 * ```
 *
 * `{s}` means one of the available subdomains (used sequentially to help with browser parallel requests per domain limitation; subdomain values are specified in options; `a`, `b` or `c` by default, can be omitted), `{z}` — zoom level, `{x}` and `{y}` — tile coordinates. `{r}` can be used to add "&commat;2x" to the URL to load retina tiles.
 *
 * You can use custom keys in the template, which will be [evaluated](#util-template) from TileLayer options, like this:
 *
 * ```
 * L.tileLayer('http://{s}.somedomain.com/{foo}/{z}/{x}/{y}.png', {foo: 'bar'});
 * ```
 */
export class TileOptions extends GridOptions {
    constructor() {
        super(...arguments);
        // @option subdomains: String|String[] = 'abc'
        // Subdomains of the tile service. Can be passed in the form of one string (where each letter is a subdomain name) or an array of strings.
        this.subdomains = ['a', 'b', 'c'];
        // @option errorTileUrl: String = ''
        // URL to the tile image to show in place of the tile that failed to load.
        this.errorTileUrl = '';
        // @option zoomOffset: Number = 0
        // The zoom number used in tile URLs will be offset with this value.
        this.zoomOffset = 0;
        // @option tms: Boolean = false
        // If `true`, inverses Y axis numbering for tiles (turn this on for [TMS](https://en.wikipedia.org/wiki/Tile_Map_Service) services).
        this.tms = false;
        // @option zoomReverse: Boolean = false
        // If set to true, the zoom number used in tile URLs will be reversed (`maxZoom - zoom` instead of `zoom`)
        this.zoomReverse = false;
        // @option detectRetina: Boolean = false
        // If `true` and user is on a retina display, it will request four tiles of half the specified size and a bigger zoom level in place of one to utilize the high resolution.
        this.detectRetina = false;
        // @option crossOrigin: Boolean|String = false
        // Whether the crossOrigin attribute will be added to the tiles.
        // If a String is provided, all tiles will have their crossOrigin attribute set to the String provided. This is needed if you want to access tile pixel data.
        // Refer to [CORS Settings](https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_settings_attributes) for valid String values.
        this.crossOrigin = false;
    }
}
export class Tile extends Grid {
    constructor(url, options) {
        super();
        this.options = new TileOptions();
        this._url = url;
        this.options.assign(options);
    }
    // @method setUrl(url: String, noRedraw?: Boolean): this
    // Updates the layer's URL template and redraws it (unless `noRedraw` is set to `true`).
    // If the URL does not change, the layer will not be redrawn unless
    // the noRedraw parameter is set to false.
    setUrl(url, noRedraw) {
        if (this._url === url && noRedraw === undefined) {
            noRedraw = true;
        }
        this._url = url;
        if (!noRedraw) {
            this.redraw();
        }
        return this;
    }
    // @method createTile(coords: Object, done?: Function): HTMLElement
    // Called only internally, overrides GridLayer's [`createTile()`](#gridlayer-createtile)
    // to return an `<img>` HTML element with the appropriate image URL given `coords`. The `done`
    // callback is called when the tile has been loaded.
    createTile(coords, done) {
        var tile = document.createElement('img');
        DomEvent.on(tile, 'load', () => {
            this._tileOnLoad(done, tile);
        });
        DomEvent.on(tile, 'error', () => {
            this._tileOnError(done, tile);
        });
        if (this.options.crossOrigin || this.options.crossOrigin === '') {
            tile.crossOrigin = this.options.crossOrigin === true ? '' : this.options.crossOrigin;
        }
        /*
         Alt tag is set to empty string to keep screen readers from reading URL and for compliance reasons
         http://www.w3.org/TR/WCAG20-TECHS/H67
        */
        tile.alt = '';
        /*
         Set role="presentation" to force screen readers to ignore this
         https://www.w3.org/TR/wai-aria/roles#textalternativecomputation
        */
        tile.setAttribute('role', 'presentation');
        tile.src = this.getTileUrl(coords);
        return tile;
    }
    // @section Extension methods
    // @uninheritable
    // Layers extending `TileLayer` might reimplement the following method.
    // @method getTileUrl(coords: Object): String
    // Called only internally, returns the URL for a tile given its coordinates.
    // Classes extending `TileLayer` can override this function to provide custom tile URL naming schemes.
    getTileUrl(coords) {
        const data = {
            r: Browser.retina ? '@2x' : '',
            s: this._getSubdomain(coords),
            x: coords.x,
            y: coords.y,
            z: this._getZoomForUrl()
        };
        if (this._map && !this._map.getCRS().infinite) {
            const invertedY = this._globalTileRange.max.y - coords.y;
            if (this.options.tms) {
                data['y'] = invertedY;
            }
            data['-y'] = invertedY;
        }
        return Util.template(this._url, data);
    }
    _tileOnLoad(done, tile) {
        // For https://github.com/Leaflet/Leaflet/issues/3332
        if (Browser.ielt9) {
            setTimeout(() => {
                done(null, tile);
            }, 0);
        }
        else {
            done(null, tile);
        }
    }
    _tileOnError(done, tile, e) {
        var errorUrl = this.options.errorTileUrl;
        if (errorUrl && tile.getAttribute('src') !== errorUrl) {
            tile.src = errorUrl;
        }
        done(e, tile);
    }
    _onTileRemove(e) {
        e.tile.onload = null;
    }
    _getZoomForUrl() {
        var zoom = this._tileZoom, maxZoom = this.options.maxZoom, zoomReverse = this.options.zoomReverse, zoomOffset = this.options.zoomOffset;
        if (zoomReverse) {
            zoom = maxZoom - zoom;
        }
        return zoom + zoomOffset;
    }
    _getSubdomain(tilePoint) {
        var index = Math.abs(tilePoint.x + tilePoint.y) % this.options.subdomains.length;
        return this.options.subdomains[index];
    }
    // stops loading all tiles in the background layer
    _abortLoading() {
        var i, tile;
        for (i in this._tiles) {
            if (this._tiles[i].coords.z !== this._tileZoom) {
                tile = this._tiles[i].el;
                tile.onload = Util.falseFn;
                tile.onerror = Util.falseFn;
                if (!tile.complete) {
                    tile.src = Util.emptyImageUrl;
                    DomUtil.remove(tile);
                    delete this._tiles[i];
                }
            }
        }
    }
    _removeTile(key) {
        var tile = this._tiles[key];
        if (!tile) {
            return;
        }
        // Cancels any pending http requests associated with the tile
        // unless we're on Android's stock browser,
        // see https://github.com/Leaflet/Leaflet/issues/137
        if (!Browser.androidStock) {
            tile.el.setAttribute('src', Util.emptyImageUrl);
        }
        return super._removeTile(key);
    }
    _tileReady(coords, err, tile) {
        if (!this._map || (tile && tile.getAttribute('src') === Util.emptyImageUrl)) {
            return;
        }
        return super._tileReady(coords, err, tile);
    }
}
